if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        this.bindComplementaryOptions();
      }

      formatMoney(cents, format = "") {
        if (typeof cents === "string") {
          cents = cents.replace(".", "");
        }
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const formatString = format || "${{amount}}";
        const match = formatString.match(placeholderRegex);
        if (!match) return String(cents / 100);

        const formatWithDelimiters = (
          number,
          precision = 2,
          thousands = ",",
          decimal = "."
        ) => {
          number = (number / 100.0).toFixed(precision);
          const parts = number.split(".");
          const dollarsAmount = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            "$1" + thousands
          );
          const centsAmount = parts[1] ? decimal + parts[1] : "";
          return dollarsAmount + centsAmount;
        };

        let value = "";
        switch (match[1]) {
          case "amount":
            value = formatWithDelimiters(cents, 2);
            break;
          case "amount_no_decimals":
            value = formatWithDelimiters(cents, 0);
            break;
          case "amount_with_comma_separator":
            value = formatWithDelimiters(cents, 2, ".", ",");
            break;
          case "amount_no_decimals_with_comma_separator":
            value = formatWithDelimiters(cents, 0, ".", ",");
            break;
          case "amount_with_apostrophe_separator":
            value = formatWithDelimiters(cents, 2, "'", ".");
            break;
          default:
            value = formatWithDelimiters(cents, 2);
        }

        return formatString.replace(placeholderRegex, value);
      }

      updateOption2PropertyValue() {
        const option2Checkbox = this.form.querySelector(
          '[data-complementary-option="2"]'
        );
        if (!option2Checkbox?.checked) return;

        const wrapper = option2Checkbox.closest(".complementary-option");
        const title = wrapper?.dataset.optionTitle;
        const unitPriceCents = Number(wrapper?.dataset.unitPriceCents);
        const moneyFormat = wrapper?.dataset.moneyFormat;
        const quantity = Number(
          this.form.querySelector('[name="complementary_quantity_2"]')?.value ||
            0
        );

        if (!title || !unitPriceCents || quantity < 1) return;

        const formattedPrice = this.formatMoney(
          unitPriceCents * quantity,
          moneyFormat
        );
        option2Checkbox.value =
          quantity > 1
            ? `${title} x ${quantity} (+${formattedPrice})`
            : `${title} (+${formattedPrice})`;
      }

      bindComplementaryOptions() {
        const option2Checkbox = this.form.querySelector(
          '[data-complementary-option="2"]'
        );
        if (!option2Checkbox) return;

        const quantityWrapper = option2Checkbox
          .closest(".complementary-option")
          ?.querySelector("[data-complementary-quantity]");
        const quantityInput = quantityWrapper?.querySelector("input");
        if (!quantityInput) return;

        const clampOption2Quantity = () => {
          if (!option2Checkbox.checked) return;
          if (Number(quantityInput.value) < 1) {
            quantityInput.value = 1;
          }
          quantityWrapper?.validateQtyRules?.();
        };

        const setQuantityState = (enabled) => {
          quantityInput.disabled = !enabled;
          quantityInput.min = enabled ? "1" : "0";
          quantityWrapper
            .querySelectorAll(".quantity-button")
            .forEach((button) => {
              button.disabled = !enabled;
            });
          if (enabled) {
            quantityInput.value = Math.max(1, Number(quantityInput.value) || 1);
          } else {
            quantityInput.value = 0;
          }
          clampOption2Quantity();
          this.updateOption2PropertyValue();
          quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
        };

        option2Checkbox.addEventListener("change", () => {
          setQuantityState(option2Checkbox.checked);
        });

        quantityInput.addEventListener("change", () => {
          clampOption2Quantity();
          this.updateOption2PropertyValue();
        });
      }

      getLineItemProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.startsWith("properties[") && key.endsWith("]")) {
            const propertyKey = key.slice(11, -1);
            if (String(value).trim()) {
              properties[propertyKey] = value;
            }
          }
        }
        return properties;
      }

      createComplementaryGroupId() {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      getComplementaryItems(formData, groupId) {
        const items = [];
        const mainVariantId = formData.get("id");
        const mainQuantity = Number(formData.get("quantity") || 1);
        const complementaryProperties = {
          _is_complementary_item: "true",
          _complementary_group_id: groupId,
          _complementary_main_variant_id: String(mainVariantId),
        };

        const option1Checkbox = this.form.querySelector(
          '[data-complementary-option="1"]'
        );
        const option1VariantId = formData.get("complementary_variant_id_1");
        if (option1Checkbox?.checked && option1VariantId) {
          items.push({
            id: Number(option1VariantId),
            quantity: mainQuantity,
            properties: {
              ...complementaryProperties,
              _complementary_option_id: "1",
            },
          });
        }

        const option2Checkbox = this.form.querySelector(
          '[data-complementary-option="2"]'
        );
        const option2VariantId = formData.get("complementary_variant_id_2");
        if (option2Checkbox?.checked && option2VariantId) {
          let quantityPerUnit = Number(
            formData.get("complementary_quantity_2") || 0
          );
          if (quantityPerUnit < 1) quantityPerUnit = 1;
          items.push({
            id: Number(option2VariantId),
            quantity: quantityPerUnit * mainQuantity,
            properties: {
              ...complementaryProperties,
              _complementary_option_id: "2",
              _complementary_quantity_per_unit: String(quantityPerUnit),
            },
          });
        }

        return items;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);

        this.updateOption2PropertyValue();

        const formData = new FormData(this.form);
        const complementaryGroupId = this.createComplementaryGroupId();
        const complementaryItems = this.getComplementaryItems(
          formData,
          complementaryGroupId
        );
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        if (complementaryItems.length > 0) {
          const mainItem = {
            id: Number(formData.get("id")),
            quantity: Number(formData.get("quantity") || 1),
          };
          const sellingPlan = formData.get("selling_plan");
          if (sellingPlan) {
            mainItem.selling_plan = Number(sellingPlan);
          }
          const properties = this.getLineItemProperties(formData);
          properties._complementary_group_id = complementaryGroupId;
          mainItem.properties = properties;

          const payload = {
            items: [mainItem, ...complementaryItems],
            sections_url: window.location.pathname,
          };
          if (this.cart) {
            payload.sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(",");
            this.cart.setActiveElement(document.activeElement);
          }
          config.headers["Content-Type"] = "application/json";
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers["Content-Type"];
          const prenom = formData.get("properties[Prénom]");
          if (prenom !== null && !String(prenom).trim()) {
            formData.delete("properties[Prénom]");
          }
          if (this.cart) {
            formData.append(
              "sections",
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append("sections_url", window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-form" });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
              if (typeof AOS !== "undefined") {
                AOS.init();
              }
            } else {
              this.cart.renderContents(response);
              if (typeof AOS !== "undefined") {
                AOS.init();
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            if (typeof AOS !== "undefined") {
              AOS.init();
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          if (typeof errorMessage === "object") {
            const formattedMessage = Object.entries(errorMessage)
              .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
              .join(" | ");
            this.errorMessage.textContent = formattedMessage;
          } else {
            this.errorMessage.textContent = errorMessage;
          }
        }
      }
    }
  );
  
  let removedElements = {
    mobile: [],
    desktop: [],
  };

  function removeElementsByScreenSize(container = document) {
    if (window.matchMedia("(max-width: 990px)").matches) {
      document.querySelectorAll(".hide-mobile").forEach((el) => {
        removedElements.desktop.push({
          parent: el.parentNode,
          element: el,
          nextSibling: el.nextSibling,
        });
        el.remove();
      });

      removedElements.mobile.forEach(({ parent, element, nextSibling }) => {
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(element, nextSibling);
        } else {
          parent.appendChild(element);
        }
      });
      removedElements.mobile = [];
    } else {
      document.querySelectorAll(".hide-desktop").forEach((el) => {
        removedElements.mobile.push({
          parent: el.parentNode,
          element: el,
          nextSibling: el.nextSibling,
        });
        el.remove();
      });
      removedElements.desktop.forEach(({ parent, element, nextSibling }) => {
        if (nextSibling && nextSibling.parentNode === parent) {
          parent.insertBefore(element, nextSibling);
        } else {
          parent.appendChild(element);
        }
      });
      removedElements.desktop = [];
    }
  }

  removeElementsByScreenSize();
  window.addEventListener("resize", removeElementsByScreenSize);
}

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this._handleResize = this._debounce(
      this.initStickyAddToCart.bind(this),
      200
    );
  }

  connectedCallback() {
    this.initStickyAddToCart();

    window.addEventListener("resize", this._handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this._handleResize);
  }

  initStickyAddToCart() {
    const img = this.querySelector(".product-content img");
    const selectField = this.querySelector('select[name="id"]');
    const addButton = this.querySelector('button[name="add"]');
    const productForm = document.querySelector(".product-form");

    if (!selectField || !addButton || !productForm) return;

    const addToCartText = addButton.getAttribute("data-add-to-cart");
    const soldOutText = addButton.getAttribute("data-sold-out");

    const checkVisibility = () => {
    const formRect = productForm.getBoundingClientRect();
    const footer = document.querySelector("footer");
    const footerRect = footer?.getBoundingClientRect();
  
    const isOutOfView = formRect.bottom <= 0;
    const isFooterVisible = footerRect && footerRect.top < window.innerHeight;
  
    if (isOutOfView && !isFooterVisible) {
      this.classList.add("show");
      document.documentElement.style.paddingBottom = `${this.clientHeight}px`;
    } else {
      this.classList.remove("show");
      document.documentElement.style.paddingBottom = "0";
    }
  };

    checkVisibility();

    window.addEventListener("scroll", checkVisibility);

    selectField.addEventListener("change", () => {
      const selectedOption = selectField.options[selectField.selectedIndex];

      if (selectedOption.dataset.img) {
        img.setAttribute("src", selectedOption.dataset.img);
      }

      if (selectedOption.getAttribute("data-available") === "false") {
        addButton.setAttribute("disabled", "true");
        addButton.querySelector("span").textContent = soldOutText;
      } else {
        addButton.removeAttribute("disabled");
        addButton.querySelector("span").textContent = addToCartText;
      }
    });

    document
      .querySelector('.product-form [name="id"]')
      ?.addEventListener("change", (e) => {
        const value = Number(e.target.value);
        if (value) {
          selectField.value = value;
        }
      });
  }

  _debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

customElements.define("sticky-add-to-cart", StickyAddToCart);